/**
 * Policy versions: the change log replayed to any version, the difference between two
 * versions, and one-line summaries. Every rule change, settings change, freeze change and
 * rollback is one version with its author. Pure.
 */
import type { FreezeWindow, Id, PolicyChange, PolicyConfig, PolicyDiffEntry, PolicyRule, PolicySettingKey, PolicySettings, PolicySnapshot } from '@orgus/protocol';
import { POLICY_SETTING_KEYS } from '@orgus/protocol';
import { SETTING_LABEL, emptyConfig } from './settings.ts';

const clone = <T>(x: T): T => (x === undefined ? x : (JSON.parse(JSON.stringify(x)) as T));

/** The policy as it was at `version` (0: empty), replaying the change log oldest first. */
export function snapshotAt(changes: readonly PolicyChange[], version: number): PolicySnapshot {
  let rules: PolicyRule[] = [];
  let config: PolicyConfig = emptyConfig();
  let at = 0;
  for (const c of [...changes].sort((a, b) => a.version - b.version)) {
    if (c.version > version) break;
    at = c.version;
    switch (c.kind) {
      case 'create':
        if (c.after) rules = [...rules.filter((r) => r.id !== c.after!.id), c.after];
        break;
      case 'update':
        if (c.after) rules = rules.some((r) => r.id === c.ruleId) ? rules.map((r) => (r.id === c.ruleId ? c.after! : r)) : [...rules, c.after];
        break;
      case 'delete':
        rules = rules.filter((r) => r.id !== c.ruleId);
        break;
      case 'settings':
      case 'freeze':
        if (c.configAfter) config = c.configAfter;
        break;
      case 'rollback':
        if (c.rules) rules = c.rules;
        if (c.configAfter) config = c.configAfter;
        break;
    }
  }
  return { version: at, rules: clone(rules), config: clone(normalizeConfig(config)) };
}

export function normalizeConfig(c: Partial<PolicyConfig> | null | undefined): PolicyConfig {
  return { org: c?.org ?? {}, projects: c?.projects ?? {}, freezes: c?.freezes ?? [] };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

function describeRuleShort(r: PolicyRule): string {
  const verb = r.decision === 'allow' ? 'Allow' : r.decision === 'deny' ? 'Deny' : 'Ask';
  const what = [r.match.tool ?? r.match.action, r.match.pattern, r.match.model ? `model ${r.match.model}` : undefined].filter(Boolean).join(' ');
  return `${verb}: ${what || 'anything'}${r.enabled === false ? ' (off)' : ''}`;
}

function settingsDiff(projectId: Id | null, a: PolicySettings | undefined, b: PolicySettings | undefined): PolicyDiffEntry[] {
  const out: PolicyDiffEntry[] = [];
  for (const k of POLICY_SETTING_KEYS as readonly PolicySettingKey[]) {
    const before = a?.[k];
    const after = b?.[k];
    if (same(before, after)) continue;
    out.push({
      area: 'setting',
      change: before === undefined ? 'added' : after === undefined ? 'removed' : 'changed',
      id: `${projectId ?? 'org'}.${k}`,
      label: SETTING_LABEL[k],
      projectId,
      ...(before !== undefined ? { before } : {}),
      ...(after !== undefined ? { after } : {}),
    });
  }
  return out;
}

function freezeLabel(w: FreezeWindow): string {
  return `Freeze "${w.name}"${w.enabled === false ? ' (off)' : ''}`;
}

/** What changed from snapshot a to snapshot b: rules, settings per layer, freeze windows. */
export function diffSnapshots(a: PolicySnapshot, b: PolicySnapshot): PolicyDiffEntry[] {
  const out: PolicyDiffEntry[] = [];
  const ra = new Map(a.rules.map((r) => [r.id, r]));
  const rb = new Map(b.rules.map((r) => [r.id, r]));
  const scopeOf = (r: PolicyRule) => (r.scope.kind === 'project' ? r.scope.projectId : null);
  const strip = (r: PolicyRule) => ({ scope: r.scope, match: r.match, decision: r.decision, reason: r.reason, enabled: r.enabled !== false });
  for (const [id, r] of rb) {
    const prev = ra.get(id);
    if (!prev) out.push({ area: 'rule', change: 'added', id, label: describeRuleShort(r), projectId: scopeOf(r), after: r });
    else if (!same(strip(prev), strip(r))) out.push({ area: 'rule', change: 'changed', id, label: describeRuleShort(r), projectId: scopeOf(r), before: prev, after: r });
  }
  for (const [id, r] of ra) if (!rb.has(id)) out.push({ area: 'rule', change: 'removed', id, label: describeRuleShort(r), projectId: scopeOf(r), before: r });

  const ca = normalizeConfig(a.config);
  const cb = normalizeConfig(b.config);
  out.push(...settingsDiff(null, ca.org, cb.org));
  for (const p of new Set([...Object.keys(ca.projects), ...Object.keys(cb.projects)])) out.push(...settingsDiff(p, ca.projects[p], cb.projects[p]));

  const fa = new Map(ca.freezes.map((w) => [w.id, w]));
  const fb = new Map(cb.freezes.map((w) => [w.id, w]));
  const scopeOfW = (w: FreezeWindow) => (w.scope.kind === 'project' ? w.scope.projectId : null);
  for (const [id, w] of fb) {
    const prev = fa.get(id);
    if (!prev) out.push({ area: 'freeze', change: 'added', id, label: freezeLabel(w), projectId: scopeOfW(w), after: w });
    else if (!same(prev, w)) out.push({ area: 'freeze', change: 'changed', id, label: freezeLabel(w), projectId: scopeOfW(w), before: prev, after: w });
  }
  for (const [id, w] of fa) if (!fb.has(id)) out.push({ area: 'freeze', change: 'removed', id, label: freezeLabel(w), projectId: scopeOfW(w), before: w });
  return out;
}

/** One line for a version in the list. */
export function summarizeChange(c: PolicyChange): string {
  if (c.summary) return c.summary;
  const rule = c.after ?? c.before;
  if (c.kind === 'create' && rule) return `Added rule ${describeRuleShort(rule)}`;
  if (c.kind === 'update' && rule) return `Changed rule ${describeRuleShort(rule)}`;
  if (c.kind === 'delete' && rule) return `Deleted rule ${describeRuleShort(rule)}`;
  if (c.kind === 'rollback') return `Rolled back to version ${c.rollbackOf ?? '?'}`;
  if (c.kind === 'freeze') return 'Changed freeze windows';
  if (c.kind === 'settings') {
    const d = [...settingsDiff(null, c.configBefore?.org, c.configAfter?.org)];
    const pa = c.configBefore?.projects ?? {};
    const pb = c.configAfter?.projects ?? {};
    for (const p of new Set([...Object.keys(pa), ...Object.keys(pb)])) d.push(...settingsDiff(p, pa[p], pb[p]));
    if (!d.length) return 'Saved settings, nothing changed';
    const where = d[0]!.projectId ? 'project' : 'org';
    return `Changed ${d.map((e) => e.label.toLowerCase()).join(', ')} (${where})`;
  }
  return c.kind;
}
